import {Injectable,} from '@angular/core';
import {LoginUserVO,} from '@models/index';
import {BehaviorSubject, NEVER, Observable, Subject, catchError, tap,} from 'rxjs';
import {
  Logger,
  MessageProvider,
  MessageStackService,
  ScreenMessageHandler,
  WindowService,
} from '@services/index';
import {HttpClient,  HttpErrorResponse,  HttpHeaders,} from '@angular/common/http';
import {LoadingService,} from '@pages/loading/loading.service';
import {MatDialog,} from '@angular/material/dialog';
import {ConfirmDialog,} from '@pages/common-component/confirm-dialog/confirm-dialog.component';

/**
 * ログイン情報サービス.
 */
@Injectable({
  providedIn: 'root',
})
export class LoginInfoService {
  private headers = new HttpHeaders({'Content-Type': 'application/json',});
  // ユーザー情報のメモリ保存
  userInfo: LoginUserVO | null;

  /** 現在のユーザーの変更を通知するためのSubject */
  public userChanged = new Subject<LoginUserVO>();
  private _subject = new BehaviorSubject<LoginUserVO>(new LoginUserVO());

  constructor(
    private windowService: WindowService,
    private screenMessageHandler: ScreenMessageHandler,
    private http: HttpClient,
    private logger: Logger,
    private loadingService: LoadingService,
    private dialog: MatDialog,
    private messageProvider: MessageProvider
  ) {
      this.userInfo = null;
  }

  /**
   * [一般開発者利用不可]
   * ログインユーザー情報の更新を伝播するSubjectを取得する
   */
  public get loginUser$(): Observable<LoginUserVO> {
    return this._subject.asObservable();
  }

  /**
   * [一般開発者利用可]
   * ログイン判定処理.
   */
  isAuthenticated(): boolean {
    const loginUserInfo = this.getLoginUserInfo();
    const accToken = this.getAccessToken();
    const refToken = this.getRefreshToken();
    return (accToken && accToken !== '' && refToken && refToken !== '')
        || (loginUserInfo.hashId != null && loginUserInfo.accessToken != null
           && loginUserInfo.refreshToken != null && !this.isExpired(loginUserInfo));
  }

  /**
   * [一般開発者利用不可]
   * ログイン処理(開発環境以外はSSO認証のため、開発環境用にアカウントのみでログインする機能を設ける
   * @param userId ユーザーID
   */
  login(hashId: string): Observable<LoginUserVO> {
    return this.http.post<LoginUserVO>('/auth/authenticate', {id:hashId,})
      .pipe(
        tap(async (res) => {
          //Msg：ログインが成功しました。
          this.logger.info('CSS01999M034I');
          this.setLoginUserInfo(res);
        })
      );
  }

  /**
   * [一般開発者利用不可]
   * @param loginUserInfo ログインユーザー情報
   */
  isExpired(loginUserInfo: LoginUserVO): boolean {
    //ログイン情報に期限切れ時間がないもしくは期限内であればOK
    if(!loginUserInfo.refreshTokenExpired ||
       loginUserInfo.refreshTokenExpired.getTime() < new Date().getTime()){
      return true;
    } else {
      return false;
    }
  }

  /**
   * [一般開発者利用可]
   * ログイン情報の取得
   */
  getLoginUserInfo(): LoginUserVO {
    if (this.userInfo && this.userInfo.accessToken && this.userInfo.refreshToken
      && this.userInfo.accessToken !== '' && this.userInfo.refreshToken !== '') {
      return this.userInfo;
    } else {
      const loginInfoFromStorage: string | null = localStorage.getItem('loginInfo');
      if (loginInfoFromStorage) {
        this.userInfo = (JSON.parse(loginInfoFromStorage) as LoginUserVO);
        this._subject.next(this.userInfo);
        return this.userInfo;
      }
    }
    return new LoginUserVO();
  }

  /**
   * [一般開発者利用不可]
   * ログイン情報(localstorageとSubscribe)の設定.
   */
  setLoginUserInfo(loginUserInfo: LoginUserVO): void {
    this.setUserInfo(loginUserInfo);
    if (loginUserInfo.accessToken && typeof loginUserInfo.accessToken !== 'undefined' && loginUserInfo.accessToken !== '') {
      // 子画面起動時にTokenが維持されるようにLocal StorageにTokenをセット
      localStorage.setItem('accToken', loginUserInfo.accessToken);
    }
    if (loginUserInfo.refreshToken && typeof loginUserInfo.refreshToken !== 'undefined' && loginUserInfo.refreshToken !== '') {
      // 子画面起動時にTokenが維持されるようにLocal StorageにTokenをセット
      localStorage.setItem('refToken', loginUserInfo.refreshToken);
    }
    // 子画面起動時にログイン状態が維持されるようにLocal Storageにログイン情報をセット
    localStorage.setItem('loginInfo', JSON.stringify(loginUserInfo));

    // ヘッダー表示のため、変更されたログインユーザー情報をSubscribeする
    this._subject.next(loginUserInfo);
  }

  /**
   * [一般開発者利用不可]
   * 当オブジェクトにログインユーザー情報を設定する
   * @param userInfo ログインユーザー情報
   * @private
   */
  private setUserInfo(userInfo: LoginUserVO): void {
    if (userInfo && userInfo.accessToken && userInfo.refreshToken
      && userInfo.accessToken !== '' && userInfo.refreshToken !== '') {
      this.userInfo = userInfo;
    }
  }

  /**
   * [一般開発者利用不可]
   * ログイン情報(localstorageとSubscribe)のクリア.
   */
  clear(): void {
    this.userInfo = null;
    localStorage.clear();
    this._subject.next(new LoginUserVO());
    if (this.userChanged !== null) {
      this.userChanged.unsubscribe();
    }
  }

  /**
   * [一般開発者利用不可]
   * アクセスTokenを取得する
   */
  getAccessToken(): string | null {
    if (this.userInfo && this.userInfo.accessToken != null) {
      return this.userInfo.accessToken;
    }
    // LocalStorageの同期
    localStorage.setItem('LAT', (new Date()).toString());
    const strgToken = localStorage.getItem('accToken');
    if (strgToken != null) {
      return strgToken;
    }
    return null;
  }

  /**
   * [一般開発者利用不可]
   * リフレッシュTokenを取得する
   */
  getRefreshToken(): string | null {
    if (this.userInfo && this.userInfo.refreshToken != null) {
      return this.userInfo.refreshToken;
    }
    // LocalStorageの同期
    localStorage.setItem('LAT', (new Date()).toString());
    const strgToken = localStorage.getItem('refToken');
    if (strgToken != null) {
      return strgToken;
    }
    return null;
  }

  /**
   * [一般開発者利用不可]
   * アクセストークンをリフレッシュし、ローカルストレージに保存する
   */
  refreshAccessToken(): Observable<LoginUserVO> {
    // リフレッシュの際、アクセストークンとリフレッシュトークンを送る
    const loginUser = new LoginUserVO();
    loginUser.accessToken = this.getAccessToken();
    loginUser.refreshToken = this.getRefreshToken();
    const refreshUrl = '/auth/refresh';
    return this.http.post<LoginUserVO>(refreshUrl, loginUser, {headers: this.headers,})
      .pipe(
        tap((res: LoginUserVO) => {
          this.logger.info('CSS01999M035I');
          this.handleAuthenticationResponse(res);
        })
      );
  }

  /**
   * [一般開発者利用不可]
   * アクセストークンをローカルストレージに保存後、ログインユーザー情報を返す
   * @param refreshInfo 変更後のログインユーザー情報
   * @private
   */
  private handleAuthenticationResponse(refreshInfo: LoginUserVO): LoginUserVO {
    const loginUserInfo = this.getLoginUserInfo();
    loginUserInfo.accessToken = <string>refreshInfo.accessToken;
    this.setLoginUserInfo(loginUserInfo);
    const user = this.getLoginUserInfo();

    return user;
  }

  /**
   * [一般開発者利用可]
   * AccessTokenリフレッシュ処理
   * ユーザーがAccessTokenリフレッシュを呼び出したい時
   * @private
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async refreshTokenManual(): Promise<void> {
    this.logger.info('CSS01999M030I');
    return await new Promise ((resolve, reject) => {
      this.refreshAccessToken().pipe(
        catchError((error: MessageStackService | HttpErrorResponse) => {
          // ローディング表示を終了する
          this.loadingService.stopAllApi();

          // トークンリフレッシュ処理のエラーでない場合、エラー内容を画面に渡す
          if (error instanceof MessageStackService ||
            (error.url && !error.url?.includes('/auth/refresh'))) {
            throw error;
          }

          if (error.status === 400 || error.status === 403) {
            this.logger.errorWithFreeText('Tokenリフレッシュの際にサーバーサイドでHTTP STATUS:400もしくは403のエラーが発生', error);
            throw error;
          } else if (error.status === 401) {
            // AccessTokenのRefreshを行おうとしたが、TokenRefresh自体の有効期限が切れてる場合は、
            // 再ログインを促すメッセージダイアログを表示する
            let isDialogOpened = true;
            const confirmationDialog = this.dialog.open(ConfirmDialog, {
              width: 'auto',
            });
            confirmationDialog.componentInstance.title =
            this.messageProvider.getMessageString({messageId: 'CSS01999M073I',}, false) || '';
            confirmationDialog.componentInstance.content =
            this.messageProvider.getMessageString({messageId: 'CSS01999M044E',}, false) || '';
            confirmationDialog.componentInstance.closeBtnText = 'OK(<u>Y</u>)';
            confirmationDialog.componentInstance.cancelBtnText = 'CANCEL(<u>N</u>)';
            confirmationDialog.componentInstance.closeBtnAccesskey = 'y';
            confirmationDialog.componentInstance.cancelBtnAccesskey = 'n';
            // 再ログインダイアログ表示
            confirmationDialog.afterClosed().subscribe((result) => {
              if (!isDialogOpened) {
                return;
              }
              isDialogOpened = false;
              // はい(Y)
              if (result) {
                this.clear();
                this.windowService.closeAll();
                // ログイン画面に遷移する
                this.windowService.transit('CSS01007');
                this.logger.errorWithFreeText('はい(Y)ボタンが押下されました。DB登録・変更処理を中止し、ログイン画面に遷移します。', error);
                return NEVER; // 後続の処理は実行しない。
              } else {
                // いいえ(N)
                this.logger.errorWithFreeText('いいえ(N)ボタンが押下されたため、DB登録・変更処理を中止します。', error);
                return NEVER; // 後続の処理は実行しない。
              }
            });
            return NEVER; // 後続の処理は実行しない。
          } else if (error.status === 502 || error.status === 503 || error.status === 504) {
            this.logger.errorWithFreeText('Tokenリフレッシュの際にサーバーサイドでHTTP STATUS:502,503もしくは504のエラーが発生', error);
            throw error;
          } else if (error.error) {
            this.logger.errorWithFreeText('Tokenリフレッシュの際にサーバーサイドでエラーが発生', error);
            throw error;
          } else {
            this.logger.errorWithFreeText('API呼び出し処理において対応するアクションがありません');
            return NEVER; // 後続の処理は実行しない。
          }
        })
      ).subscribe({
        next: () => {
          resolve();
        },
        error: (err) => {
          reject(err);
        },
      });
    })

  }
}
